import React, { useRef, useEffect, useState, useCallback } from 'react';
import * as d3 from 'd3';

const NODE_COLORS = {
  SERVICE: '#3b82f6',
  TEAM: '#22c55e',
  PATTERN: '#a855f7',
  API: '#eab308',
  REGULATION: '#ef4444',
  DEFAULT: '#6b7280',
};

const LINK_COLORS = {
  DEPENDS_ON: '#6b7280',
  OWNED_BY: '#22c55e',
  USES_PATTERN: '#a855f7',
  COMPLIES_WITH: '#ef4444',
  INTEGRATES_WITH: '#3b82f6',
  DEFAULT: '#4a4a4a',
};

function ForceGraph({ entities, relationships, selectedEntity, onSelectEntity, width, height }) {
  const svgRef = useRef(null);
  const [simulation, setSimulation] = useState(null);

  const buildGraphData = useCallback(() => {
    const nodes = entities.map(entity => ({
      id: entity.id,
      name: entity.name,
      type: entity.entity_type,
      description: entity.description,
      ...entity,
    }));

    const nodeIds = new Set(nodes.map(n => n.id));
    const links = relationships
      .filter(rel => nodeIds.has(rel.source_id) && nodeIds.has(rel.target_id))
      .map(rel => ({
        source: rel.source_id,
        target: rel.target_id,
        type: rel.rel_type,
        description: rel.description,
      }));

    return { nodes, links };
  }, [entities, relationships]);

  useEffect(() => {
    if (!svgRef.current || !entities.length) return;

    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();

    const { nodes, links } = buildGraphData();

    // Create container group for zoom
    const g = svg.append('g');

    // Add zoom behavior
    const zoom = d3.zoom()
      .scaleExtent([0.2, 4])
      .on('zoom', (event) => {
        g.attr('transform', event.transform);
      });

    svg.call(zoom);

    // Create force simulation
    const sim = d3.forceSimulation(nodes)
      .force('link', d3.forceLink(links).id(d => d.id).distance(120))
      .force('charge', d3.forceManyBody().strength(-400))
      .force('center', d3.forceCenter(width / 2, height / 2))
      .force('collision', d3.forceCollide().radius(40));

    // Create arrow markers for directed edges
    const defs = svg.append('defs');
    Object.keys(LINK_COLORS).forEach(type => {
      defs.append('marker')
        .attr('id', `arrow-${type}`)
        .attr('viewBox', '0 -5 10 10')
        .attr('refX', 25)
        .attr('refY', 0)
        .attr('markerWidth', 6)
        .attr('markerHeight', 6)
        .attr('orient', 'auto')
        .append('path')
        .attr('fill', LINK_COLORS[type] || LINK_COLORS.DEFAULT)
        .attr('d', 'M0,-5L10,0L0,5');
    });

    // Create links
    const link = g.append('g')
      .attr('class', 'links')
      .selectAll('line')
      .data(links)
      .enter()
      .append('line')
      .attr('stroke', d => LINK_COLORS[d.type] || LINK_COLORS.DEFAULT)
      .attr('stroke-opacity', 0.6)
      .attr('stroke-width', 2)
      .attr('marker-end', d => `url(#arrow-${d.type})`);

    // Create node groups
    const node = g.append('g')
      .attr('class', 'nodes')
      .selectAll('g')
      .data(nodes)
      .enter()
      .append('g')
      .attr('class', 'graph-node')
      .call(d3.drag()
        .on('start', dragstarted)
        .on('drag', dragged)
        .on('end', dragended));

    // Add circles to nodes
    node.append('circle')
      .attr('r', d => d.type === 'SERVICE' ? 20 : 16)
      .attr('fill', d => NODE_COLORS[d.type] || NODE_COLORS.DEFAULT)
      .attr('stroke', d => selectedEntity === d.id ? '#a855f7' : '#1a1a1a')
      .attr('stroke-width', d => selectedEntity === d.id ? 3 : 2);

    // Add labels
    node.append('text')
      .attr('dy', 35)
      .attr('text-anchor', 'middle')
      .attr('fill', '#d4d4d4')
      .attr('font-size', '10px')
      .attr('font-family', 'JetBrains Mono, monospace')
      .text(d => d.name.length > 15 ? d.name.substring(0, 15) + '...' : d.name);

    // Add icons/text inside nodes
    node.append('text')
      .attr('dy', 4)
      .attr('text-anchor', 'middle')
      .attr('fill', 'white')
      .attr('font-size', '12px')
      .attr('font-weight', 'bold')
      .text(d => d.type.charAt(0));

    // Add tooltips
    node.append('title')
      .text(d => `${d.name}\n${d.type}\n${d.description || ''}`);

    // Click handler
    node.on('click', (event, d) => {
      event.stopPropagation();
      onSelectEntity(d.id);
    });

    // Click on background to deselect
    svg.on('click', () => {
      onSelectEntity(null);
    });

    // Simulation tick
    sim.on('tick', () => {
      link
        .attr('x1', d => d.source.x)
        .attr('y1', d => d.source.y)
        .attr('x2', d => d.target.x)
        .attr('y2', d => d.target.y);

      node.attr('transform', d => `translate(${d.x},${d.y})`);
    });

    function dragstarted(event, d) {
      if (!event.active) sim.alphaTarget(0.3).restart();
      d.fx = d.x;
      d.fy = d.y;
    }

    function dragged(event, d) {
      d.fx = event.x;
      d.fy = event.y;
    }

    function dragended(event, d) {
      if (!event.active) sim.alphaTarget(0);
      d.fx = null;
      d.fy = null;
    }

    setSimulation(sim);

    return () => {
      sim.stop();
    };
  }, [entities, relationships, width, height, selectedEntity, onSelectEntity, buildGraphData]);

  // Update node selection styling when selectedEntity changes
  useEffect(() => {
    if (!svgRef.current) return;

    const svg = d3.select(svgRef.current);
    svg.selectAll('.graph-node circle')
      .attr('stroke', d => selectedEntity === d.id ? '#a855f7' : '#1a1a1a')
      .attr('stroke-width', d => selectedEntity === d.id ? 3 : 2);
  }, [selectedEntity]);

  return (
    <svg
      ref={svgRef}
      width={width}
      height={height}
      className="bg-dark-900"
    />
  );
}

export default ForceGraph;
